<script setup lang="ts">
import LightBulbIcon from '@/assets/icons/LightBulbIcon.vue';
import WarningBadge from '@/core/badges/WarningBadge.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import { computed } from 'vue';

export interface IClassFormFooterConfig {
  nextBtnLabel?: string;
  quitBtnLabel?: string;
  previousBtnLabel?: string;
  helpText?: string;
  hintLineHeightPx?: number;
  buttonSpacing?: 'end' | 'between';
}

const emit = defineEmits<{
  'click:previous': [];
  'click:next': [];
}>();

const props = defineProps<{
  footerConfig: IClassFormFooterConfig;
  warningText?: string | null;
  formValid: boolean;
  buttonType?: 'submit' | 'button';
}>();

const buttonSpacing = computed(() => props.footerConfig.buttonSpacing ?? 'end');
</script>

<template>
  <div class="flex flex-col gap-md w-full">
    <WarningBadge v-if="warningText" :label="warningText" class="mt-auto" />
    <div class="flex w-full flex-col gap-md md:flex-row md:items-center md:justify-between">
      <CustomButton v-if="footerConfig.quitBtnLabel" class="w-min" theme="secondary" @click="emit('click:previous')">
        {{ footerConfig.quitBtnLabel }}
      </CustomButton>
      <div v-if="footerConfig.helpText" class="hidden md:flex items-start gap-sm">
        <LightBulbIcon class="size-icon-md text-primary flex-shrink-0" />
        <!-- eslint-disable vue/no-v-html -->
        <span
          class="text-muted"
          :class="footerConfig.hintLineHeightPx ? '' : 'leading-5'"
          :style="footerConfig.hintLineHeightPx ? { lineHeight: `${footerConfig.hintLineHeightPx}px` } : undefined"
          v-html="footerConfig.helpText"
        ></span>
        <!-- eslint-enable vue/no-v-html -->
      </div>
      <div v-else class="w-full md:w-auto">
        <slot />
      </div>
      <div
        :class="['flex gap-md md:ml-auto max-md:w-full justify-end', buttonSpacing === 'between' && '!justify-between']"
      >
        <CustomButton
          v-if="footerConfig.previousBtnLabel"
          class="w-min whitespace-nowrap"
          theme="secondary"
          @click="emit('click:previous')"
        >
          {{ footerConfig.previousBtnLabel }}
        </CustomButton>

        <CustomButton
          v-if="footerConfig.nextBtnLabel"
          :disabled="!formValid"
          class="w-min whitespace-nowrap"
          :type="buttonType ?? 'submit'"
          @click="emit('click:next')"
        >
          {{ footerConfig.nextBtnLabel }}
        </CustomButton>
      </div>
    </div>
  </div>
</template>
